import { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';

import { LineSeparator } from '@/components/LineSeparator';
import { useHasMaxOddsError } from '@/feature/betslip-sbk/components/parlay-card/hooks/use-parlay-card-alert';
import { useActiveSelectionCount, useSelectionCount } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { ComboBetType, ComboBetTypes } from '@/feature/betslip-sbk/types';
import { maxSingleOddsSelector } from '@/feature/betslip-sbk/utils/betslip-selectors';
import { getComboBetType, getComboOdds, isComboBetSelectionsValid } from '@/feature/betslip-sbk/utils/betslip-utils';
import { getLayoutAnimationsTransition } from '@/utils/animations/layout-animations-utils';

import { PickSlipBarLeftContent } from './PickSlipBarLeftContent';
import { PickSlipBarRightContent } from './PickSlipBarRightContent';

export type BetType = 'SINGLE' | 'PARLAY' | 'SGP' | 'SGP+' | 'GENERIC';

export const isSgpBetType = (betType: BetType): boolean => betType === 'SGP' || betType === 'SGP+';

export const isParlayBetType = (betType: BetType): boolean =>
    betType === 'PARLAY' || betType === 'SGP' || betType === 'SGP+';

export const HYPOTHETICAL_STAKE = 10;

type PickBarData = {
    betType: BetType;
    payout: number;
    selectionCount: number;
    isSgpFetching: boolean;
    hasOddsMaxedOutError: boolean;
};

const determineBetType = (
    comboBetType: ComboBetType,
    selectionCount: number,
    activeSelectionCount: number,
    hasOtherComboErrors: boolean
): BetType => {
    if (selectionCount === 1 && activeSelectionCount === 1) {
        return 'SINGLE';
    }

    // Handle parlay types (only if no other combo errors)
    if (activeSelectionCount >= 2 && !hasOtherComboErrors) {
        if (comboBetType === ComboBetTypes.SGP_Plus) {
            return 'SGP+';
        }
        if (comboBetType === ComboBetTypes.SGP) {
            return 'SGP';
        }
        return 'PARLAY';
    }

    return 'GENERIC';
};

const usePickBarData = (): PickBarData => {
    // Fetch all data upfront
    const selectionCount = useSelectionCount();
    const activeSelectionCount = useActiveSelectionCount();
    const isSgpFetching = useSbkBetSlipStore(state => state.isSgpFetching);
    const comboBetType = useSbkBetSlipStore(state => getComboBetType(state));
    const maxParlayOdds = useSbkBetSlipStore(state => getComboOdds(state));
    const maxSinglesOdds = useSbkBetSlipStore(state => maxSingleOddsSelector(state));

    // Process alerts
    const hasOddsMaxedOutError = useHasMaxOddsError();
    const hasOtherComboErrors = useSbkBetSlipStore(state => !isComboBetSelectionsValid(state));

    // Determine bet type
    const betType = determineBetType(comboBetType, selectionCount, activeSelectionCount, hasOtherComboErrors);

    // Calculate display values based on bet type
    switch (betType) {
        case 'SINGLE':
            return {
                betType,
                payout: maxSinglesOdds * HYPOTHETICAL_STAKE,
                selectionCount: 1,
                isSgpFetching,
                hasOddsMaxedOutError: false,
            };
        case 'PARLAY':
        case 'SGP':
        case 'SGP+':
            return {
                betType,
                payout: maxParlayOdds * HYPOTHETICAL_STAKE,
                selectionCount: activeSelectionCount,
                isSgpFetching,
                hasOddsMaxedOutError,
            };
        case 'GENERIC':
        default:
            return {
                betType: 'GENERIC',
                payout: 0,
                selectionCount,
                isSgpFetching,
                hasOddsMaxedOutError: false,
            };
    }
};

export const PickSlipBarContent = () => {
    const [hasRendered, setHasRendered] = useState(false);

    useEffect(() => {
        setHasRendered(true);
    }, []);

    const { betType, payout, selectionCount, isSgpFetching, hasOddsMaxedOutError } = usePickBarData();

    const layoutTransition = getLayoutAnimationsTransition();

    return (
        <Animated.View layout={layoutTransition} style={styles.container}>
            <PickSlipBarLeftContent betType={betType} selectionCount={selectionCount} hasRendered={hasRendered} />
            <LineSeparator vertical style={styles.lineSeparator} />
            <PickSlipBarRightContent
                betType={betType}
                hasOddsMaxedOutError={hasOddsMaxedOutError}
                isSgpFetching={isSgpFetching}
                payout={payout}
            />
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    lineSeparator: {
        height: 30,
    },
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
});
